import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { indexTagsCategory } from '@services/queries/tagsCategory';
import { updateTag } from '@services/queries/tags/updateTag/updateTag';
import { ITag } from '@models/Tag.model';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { useEffect } from 'react';
import { useState } from '@hookstate/core';

import { CategoryColorBadge } from '@components/CategoryColorBadge/CategoryColorBadge';
import {
  Container,
  Overlay,
  Close,
  Header,
  Title,
  Confirm,
  Line,
  Form,
  Divider,
  Label,
  Input,
  Textarea,
  Error,
} from './EditTagDialog.styles';

interface IEditTagDialog {
  open: boolean;
  onClose: () => void;
  tagId: ITag;
}

interface IFormData {
  name: string;
  description: string;
  tagCategoryId: string;
}

export const EditTagDialog = ({ open, onClose, tagId }: IEditTagDialog) => {
  const portalDiv = document.getElementById('portal');

  const isEdited = useState<boolean>(false);

  const queryClient = useQueryClient();

  const { data } = useQuery(['tagsCategory'], () => indexTagsCategory());

  const Options = data?.map(option => {
    return {
      value: option.id,
      label: <CategoryColorBadge color={option.color} name={option.name} />,
    };
  });

  const schema = yup.object().shape({
    name: yup.string().required(),
    description: yup.string(),
    tagCategoryId: yup.string().required(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { isValid, errors },
    reset,
    setError,
    clearErrors,
    control,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: tagId.name,
      tagCategoryId: tagId.tagCategoryId,
      description: tagId.description || '',
    },
    mode: 'onChange',
  });

  const close = () => {
    onClose();
    reset();
    isEdited.set(false);
    clearErrors('name');
  };

  useEffect(() => {
    setValue('name', tagId.name);
    setValue('description', tagId.description || '');
    setValue('tagCategoryId', tagId.tagCategoryId);
  }, [tagId]);

  const mutationUpdateTag = useMutation(updateTag, {
    onSuccess: () => {
      queryClient.invalidateQueries('tags');
      queryClient.invalidateQueries('tagCategories');
      close();
      setValue('name', '');
      setValue('description', '');
      setValue('tagCategoryId', '');
    },
    onError: (error: any) => {
      if (
        error.response?.data?.message ===
        'There is already a Tag with this name for this category'
      ) {
        setError('name', {
          type: 'validate',
          message: 'There is already a Tag with this name for this category',
        });
      }
    },
  });

  const onSubmit = (Tag: IFormData) => {
    if (!mutationUpdateTag.isLoading) {
      if (data)
        mutationUpdateTag.mutate({
          id: tagId.id,
          name: Tag.name,
          description: Tag.description,
          tagCategoryId: Tag.tagCategoryId,
        });
    }
  };

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>Edit Tag</Title>
              <Close type="button" onClick={() => close()}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>
            <Divider height="28px" />

            <Form onSubmit={handleSubmit(onSubmit)}>
              <Label className="mandatory">Tag name</Label>

              <Input
                placeholder="Ex. Studio"
                autoFocus
                {...register('name')}
                onChange={e => {
                  isEdited.set(true);
                  register('name').onChange(e);
                }}
                isError={
                  errors.name &&
                  errors.name.message ===
                    'There is already a Tag with this name for this category'
                }
              />
              {errors.name &&
                errors.name.message ===
                  'There is already a Tag with this name for this category' && (
                  <Error>{errors.name.message}</Error>
                )}

              <Divider height="16px" />

              <Label className="mandatory">Category</Label>

              <Controller
                control={control}
                name="tagCategoryId"
                render={({ field: { onChange, value } }) => (
                  <CustomPicker
                    options={Options}
                    placeholder="Select a Category"
                    value={Options?.find(c => c.value === value)}
                    onChange={(val: any) => {
                      onChange(val.value);
                      isEdited.set(true);
                    }}
                  />
                )}
              />

              <Label className="optional">Description (optional)</Label>

              <Textarea
                placeholder="Description"
                {...register('description')}
                onChange={e => {
                  isEdited.set(true);
                  register('description').onChange(e);
                }}
              />

              <Line>
                <Confirm
                  type="submit"
                  disabled={!isValid || mutationUpdateTag.isLoading}
                >
                  Save
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
